"use client";

import * as React from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type TechRow = {
  stack: string;
  category: string;
  impact: string;
  status: string;
  recommended: boolean;
};
const categories = [{
  id: "ota",
  label: "OTA Management"
}, {
  id: "pms",
  label: "PMS Integration"
}, {
  id: "revenue",
  label: "Revenue Tools"
}, {
  id: "analytics",
  label: "Data Analytics"
}];
const otaRows: TechRow[] = [{
  stack: "Booking.com",
  category: "Channel",
  impact: "High Visibility",
  status: "Active",
  recommended: false
}, {
  stack: "Expedia",
  category: "Channel",
  impact: "Global Reach",
  status: "Active",
  recommended: false
}, {
  stack: "Airbnb",
  category: "Channel",
  impact: "Niche Growth",
  status: "Optimized",
  recommended: true
}, {
  stack: "Agoda",
  category: "Channel",
  impact: "Asian Market",
  status: "Pending",
  recommended: false
}, {
  stack: "Direct",
  category: "Channel",
  impact: "Zero Commission",
  status: "Priority",
  recommended: false
}];
const pmsRows: TechRow[] = [{
  stack: "Cloudbeds",
  category: "System",
  impact: "Unified Ops",
  status: "Integrated",
  recommended: false
}, {
  stack: "Mews",
  category: "System",
  impact: "Modern API",
  status: "Recommended",
  recommended: false
}, {
  stack: "Oracle",
  category: "System",
  impact: "Enterprise Scale",
  status: "Legacy",
  recommended: true
}, {
  stack: "Little Hotelier",
  category: "System",
  impact: "Boutique Focus",
  status: "Active",
  recommended: false
}, {
  stack: "SiteMinder",
  category: "System",
  impact: "Rate Sync",
  status: "Active",
  recommended: false
}];
const revenueRows: TechRow[] = [{
  stack: "Duetto",
  category: "Revenue",
  impact: "AI Pricing",
  status: "Advanced",
  recommended: false
}, {
  stack: "IDeaS",
  category: "Revenue",
  impact: "Forecasting",
  status: "Enterprise",
  recommended: false
}, {
  stack: "Pace",
  category: "Revenue",
  impact: "Real-time",
  status: "Growth",
  recommended: true
}, {
  stack: "Atomize",
  category: "Revenue",
  impact: "Automation",
  status: "Active",
  recommended: false
}, {
  stack: "RateGain",
  category: "Revenue",
  impact: "Market Intel",
  status: "Active",
  recommended: false
}];
const analyticsRows: TechRow[] = [{
  stack: "Google Analytics",
  category: "Data",
  impact: "Web Traffic",
  status: "Essential",
  recommended: false
}, {
  stack: "Tableau",
  category: "Data",
  impact: "Visualization",
  status: "Strategic",
  recommended: false
}, {
  stack: "Power BI",
  category: "Data",
  impact: "Reporting",
  status: "Integrated",
  recommended: true
}, {
  stack: "Looker",
  category: "Data",
  impact: "Deep Insights",
  status: "Active",
  recommended: false
}, {
  stack: "Custom Dash",
  category: "Data",
  impact: "KPI Tracking",
  status: "Custom",
  recommended: false
}];
const techData: Record<string, TechRow[]> = {
  ota: otaRows,
  pms: pmsRows,
  revenue: revenueRows,
  analytics: analyticsRows
};
function TechTable({
  rows
}: {
  rows: TechRow[];
}) {
  return <TooltipProvider delayDuration={150}>
      <Table className="border-collapse">
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground sticky left-0 bg-background">
              Platform
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Category
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Impact
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => <Tooltip key={i}>
              <TooltipTrigger asChild>
                <TableRow data-index={i} className="border-b border-dashed border-border odd:bg-muted/20 transition-colors duration-200 hover:bg-accent/40 cursor-default">
                  <TableCell className={row.recommended ? "sticky left-0 bg-secondary text-secondary-foreground font-default font-semibold text-base" : "sticky left-0 bg-background font-default text-base text-foreground"}>
                    {row.stack}
                  </TableCell>
                  <TableCell className="font-default text-base text-foreground">
                    {row.category}
                  </TableCell>
                  <TableCell className="font-default text-base text-foreground">
                    {row.impact}
                  </TableCell>
                  <TableCell className="font-default text-base text-foreground">
                    {row.status}
                  </TableCell>
                </TableRow>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground font-default text-[0.7rem] uppercase tracking-[0.12em]">
                {row.impact} Analysis
              </TooltipContent>
            </Tooltip>)}
        </TableBody>
      </Table>
    </TooltipProvider>;
}
export default function TechIntegrationArchitecture() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1935" id="techintegrationarchitecture" className="bg-background text-foreground py-24 md:py-36 px-5 md:px-10">
      <div className="mx-auto max-w-[1320px]">
        <div ref={ref} className={"relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end transition-all duration-700 " + (isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-0 select-none font-default font-bold text-foreground/[0.06] leading-none text-[8rem] md:text-[12rem]">
            02
          </span>

          <div className="lg:col-span-8 relative z-10">
            <p className="font-default text-xs md:text-sm uppercase tracking-[0.22em] text-muted-foreground mb-6">
              § 02 / TECH STACK ARCHITECTURE
            </p>
            <h2 className="font-default font-semibold leading-[0.98] tracking-[-0.02em] text-[clamp(2rem,4.5vw,3.75rem)] text-foreground">
              Hospitality Tech,{" "}
              <span className="relative inline-block">
                Optimized
                <span className="absolute -bottom-1 left-0 h-[3px] w-full bg-secondary" />
                <span className="absolute -bottom-1 right-[-6px] h-[7px] w-[7px] rounded-lg bg-secondary" />
              </span>{" "}
              For Revenue
            </h2>
            <p className="mt-6 max-w-xl font-default text-base md:text-lg leading-relaxed text-muted-foreground">
              We audit and integrate your hotel's technology stack to ensure seamless data flow, automated revenue management, and maximum OTA performance.
            </p>
          </div>

          <div className="lg:col-span-4 relative z-10">
            <Tabs defaultValue="ota" className="w-full">
              <TabsList className="flex w-full flex-wrap h-auto gap-1 bg-muted p-1 rounded-lg">
                {categories.map((cat, i) => <TabsTrigger key={cat.id} data-index={i} value={cat.id} className="flex-1 font-default text-xs uppercase tracking-[0.1em] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
                    {cat.label}
                  </TabsTrigger>)}
              </TabsList>

              {categories.map((cat, i) => <TabsContent key={cat.id} data-index={i} value={cat.id} className="mt-10 animate-in fade-in-0 duration-300">
                  <div className="block md:hidden">
                    <div className="rounded-lg border border-dashed border-border p-1">
                      <ScrollArea className="w-full whitespace-nowrap">
                        <div className="min-w-[480px] p-3">
                          <TechTable rows={techData[cat.id]} />
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </div>
                  </div>
                </TabsContent>)}
            </Tabs>
          </div>
        </div>

        <div className="my-10 hidden md:block border-t border-dashed border-border" />

        <div className="hidden md:block">
          <Tabs defaultValue="ota" className="w-full">
            <TabsList className="mb-8 inline-flex h-auto gap-1 bg-muted p-1 rounded-lg">
              {categories.map((cat, i) => <TabsTrigger key={cat.id} data-index={i} value={cat.id} className="font-default text-xs uppercase tracking-[0.12em] px-5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
                  {cat.label}
                </TabsTrigger>)}
            </TabsList>

            {categories.map((cat, i) => <TabsContent key={cat.id} data-index={i} value={cat.id} className="animate-in fade-in-0 duration-300">
                <div className="rounded-lg border border-dashed border-border p-4 md:p-6 bg-card/20">
                  <TechTable rows={techData[cat.id]} />
                </div>
                <p className="mt-4 font-default text-[0.7rem] uppercase tracking-[0.12em] text-muted-foreground">
                  Gold highlights indicate our recommended strategic partners for your property.
                </p>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}